<template>
  <div class="give-medicine-container">
    <!-- 顶部区域 -->
    <div class="top-area">
      <!-- 搜索筛选区 -->
      <div class="search-panel">
        <div class="search-left">
          <span class="label">搜索维度</span>
          <div class="dimension-group">
            <span class="dimension-item">患者姓名</span>
            <span class="dimension-item">健康ID</span>
            <span class="dimension-item">处方编号</span>
            <span class="dimension-item dimension-active">就诊号 <i class="el-icon-arrow-down"></i></span>
          </div>
        </div>
        <div class="search-middle">
          <el-input
            v-model="searchKeyword"
            placeholder="请输入搜索内容"
            size="small"
            class="search-input"
            clearable
          ></el-input>
        </div>
        <div class="search-right">
          <el-button type="primary" size="small" icon="el-icon-Ssearch" @click="fetchPatients">查询</el-button>
          <el-button size="small" @click="clearSearch">清空</el-button>
        </div>
      </div>

      <!-- 患者信息卡片 -->
      <div class="patient-card" v-if="currentPatient && currentPatient.patientId">
        <div class="card-row">
          <div class="card-item"><span class="label">患者姓名：</span><span class="val">{{ currentPatient.patientName || '-' }}</span></div>
          <div class="card-item"><span class="label">性别：</span><span class="val">{{ currentPatient.sex == 1 ? '男' : currentPatient.sex == 2 ? '女' : '-' }}</span></div>
          <div class="card-item"><span class="label">年龄：</span><span class="val">{{ currentPatient.age || '-' }}岁</span></div>
          <div class="card-item"><span class="label">健康卡号：</span><span class="val">{{ currentPatient.healthId || '-' }}</span></div>
          <div class="card-item"><span class="label">联系电话：</span><span class="val">{{ currentPatient.tel || '-' }}</span></div>
          <div class="card-item"><span class="label">就诊科室：</span><span class="val">{{ currentPatient.deptName || '-' }}</span></div>
          <div class="card-item"><span class="label">主治医生：</span><span class="val">{{ currentPatient.doctorName || '-' }}</span></div>
        </div>
        <div class="card-row">
          <div class="card-item"><span class="label">处方编号：</span><span class="val">{{ currentPatient.orderNo || '-' }}</span></div>
          <div class="card-item"><span class="label">处方类型：</span><span class="val">{{ currentPatient.orderNoType || '-' }}</span></div>
          <div class="card-item"><span class="label">开单时间：</span><span class="val">{{ currentPatient.orderedAt || '-' }}</span></div>
          <div class="card-item"><span class="label">西医诊断：</span><span class="val">{{ currentPatient.icdName || '-' }}</span></div>
          <div class="card-item"><span class="label">处方备注：</span><span class="val">{{ currentPatient.remark || '无特殊禁忌' }}</span></div>
          <div class="card-item"><span class="label">处方总金额：</span><span class="val text-red">¥{{ (currentPatient.totalAmount || 0).toFixed(2) }}</span></div>
          <div class="card-item"><span class="label">药品总数量：</span><span class="val">{{ allItemsCount }}项</span></div>
        </div>
      </div>
      <div v-else class="patient-card empty">
        <span>请从下方列表选择患者以查看详情</span>
      </div>
    </div>

    <!-- 主体双栏区域 -->
    <div class="main-body">
      <!-- 左侧列表区 -->
      <div class="left-panels">
        <!-- 上部分：待发药患者列表 -->
        <div class="list-panel top-list">
          <div class="panel-title">待发药患者列表</div>
          <div class="list-scroll" v-loading="loading">
            <div
              v-for="(patient, idx) in patientList"
              :key="idx"
              class="list-item"
              :class="{ active: currentPatient && currentPatient.patientId === patient.patientId }"
              @click="selectPatient(patient)"
            >
              <div class="item-info">
                <span class="blue-square"></span>
                <span class="name">{{ patient.patientName || '-' }}</span>
                <span class="gender" :class="patient.sex == 1 ? 'male' : 'female'">
                  {{ patient.sex == 1 ? '男' : patient.sex == 2 ? '女' : '-' }}
                </span>
                <span class="age">{{ patient.age || '--' }}岁</span>
                <span class="desc">待发 {{ patient.rxCount || '-' }}张</span>
              </div>
            </div>
            <div v-if="patientList.length === 0 && !loading" class="empty-text">暂无数据</div>
          </div>
        </div>

        <!-- 下部分：选中患者待发处方列表 -->
        <div class="list-panel bottom-list">
          <div class="panel-title">选中患者待发处方列表</div>
          <div class="pres-table-header">
            <div class="th">序号</div>
            <div class="th">处方编号</div>
            <div class="th">金额</div>
            <div class="th">医生</div>
          </div>
          <div class="list-scroll pres-scroll" v-if="prescriptionList && prescriptionList.length > 0">
            <div
              v-for="(pres, idx) in prescriptionList"
              :key="idx"
              class="list-item pres-item"
              :class="{ active: activePrescriptionIndex === idx }"
              @click="selectPrescription(idx)"
            >
              <div class="pres-col pres-index">{{ idx + 1 }}</div>
              <div class="pres-col pres-no" :title="pres.orderNo">{{ pres.orderNo || '-' }}</div>
              <div class="pres-col pres-money">¥{{ (pres.totalAmount || 0).toFixed(2) }}</div>
              <div class="pres-col pres-doctor">{{ pres.doctorName || pres.orderingDoctorId || '-' }}</div>
            </div>
          </div>
          <div v-else class="empty-text">请选择待发药患者</div>
        </div>
      </div>

      <!-- 右侧明细区 -->
      <div class="right-panel">
        <div class="right-header">药品明细与配发校验</div>
        <div class="table-wrapper">
          <el-table
            :data="activePrescriptionItems || []"
            border
            size="small"
            style="width: 100%"
            height="100%"
          >
            <el-table-column label="类型" width="70" align="center">
              <template slot-scope="scope">
                <el-tag size="mini" :type="getItemTypeTagType(scope.row.itemType)">
                  {{ formatItemType(scope.row.itemType) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="itemName" label="药品名称" min-width="150"></el-table-column>
            <el-table-column prop="specification" label="规格" width="140" show-overflow-tooltip></el-table-column>
            <el-table-column prop="quantity" label="开单总量" width="90" align="center"></el-table-column>
            <el-table-column prop="unitName" label="单位" width="60" align="center"></el-table-column>
            <el-table-column label="用法" width="100" align="center">
              <template slot-scope="scope">
                <el-tag size="mini" type="warning">{{ scope.row.routeName || scope.row.routeId || '-' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="频次" width="100" align="center">
              <template slot-scope="scope">
                <el-tag size="mini" type="warning">{{ scope.row.frequencyName || scope.row.frequencyId || '-' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="天数" width="100" align="center">
              <template slot-scope="scope">
                <el-tag size="mini" type="danger">{{ scope.row.periodDays || 0 }}天</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="180" align="center" fixed="right">
              <template slot-scope="scope">
                <div class="op-action-group">
                  <!-- 非西药直接显示已配发完成 -->
                  <template v-if="scope.row.itemType === 2">
                    <el-link type="success" :underline="false">已配发完成</el-link>
                  </template>

                  <!-- 追溯码药品 -->
                  <template v-else-if="scope.row.isBar === 1">
                    <el-link type="warning" :underline="false" @click="handleTraceCode(scope.row)">
                      {{ scope.row.traceDisplayText || '扫码采集追溯码' }}
                    </el-link>
                  </template>

                  <!-- ★★★ 修正：西药 + 批次选择（非追溯码），仅当系统开启批次选择开关时显示 ★★★ -->
                  <template v-else-if="scope.row.itemType === 1 || needStockBatchSelect || scope.row.itemType === 5">
                    <el-link type="primary" :underline="false" v-if="scope.row.selectedBatchNum" @click="handleBatchSelect(scope.row)">{{ scope.row.selectedBatchNum }}</el-link>
                    <el-link type="primary" :underline="false" v-else @click="handleBatchSelect(scope.row)">选择批次</el-link>
                  </template>

                  <!-- 其他情况（非追溯码且开关未开启）显示已配发完成 -->
                  <el-tag size="mini" type="success" v-else>已配发完成</el-tag>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
        
        <!-- 底部统计与操作 -->
        <div class="right-footer">
          <div class="footer-stats">
            <span class="stat-item">药品总数量：<span class="stat-val">{{ totalDrugCount }}</span></span>
            <span class="stat-item">处方总金额：<span class="stat-val text-red">¥{{ activePrescriptionTotal || '0.00' }}</span></span>
          </div>
          <div class="footer-btns">
            <el-button type="primary" size="small" :disabled="isGiveMedicineDisabled" @click="handleGiveMedicine">发药</el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 项目详情模态框 -->
    <el-dialog title="项目详情基本档案" :visible.sync="dialogVisible" width="600px" v-loading="loadingDrug">
      <div v-if="drugDetail" class="drug-detail-content">
        <div class="detail-row"><span class="label">项目ID：</span><span>{{ drugDetail.id || '-' }}</span></div>
        <div class="detail-row"><span class="label">项目名称：</span><span>{{ drugDetail.drugName || drugDetail.name || '-' }}</span></div>
        <div class="detail-row"><span class="label">规格：</span><span>{{ drugDetail.drugSpec || drugDetail.spec || '-' }}</span></div>
      </div>
      <div v-else-if="!loadingDrug">暂无数据</div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">关 闭</el-button>
      </span>
    </el-dialog>

    <!-- 选择批次模态框 -->
    <el-dialog
      :title="batchCurrentItemName + ' - 选择批次进行发药'"
      :visible.sync="batchDialogVisible"
      width="950px"
      :close-on-click-modal="false"
      custom-class="batch-dialog"
    >
      <div class="batch-dialog-content">
        <el-table
          :data="batchList"
          border
          style="width: 100%"
          height="300"
        >
          <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
          
          <el-table-column label="药品名称" width="160">
            <template slot-scope="scope">
              <span v-if="scope.row.manuLotnum">{{ batchCurrentItemName }}</span>
            </template>
          </el-table-column>
          
          <el-table-column label="发药数量" width="130" align="center">
            <template slot-scope="scope">
              <div style="display:flex; align-items:center; justify-content:center; gap:4px;">
                <el-input 
                  v-model="scope.row.chgCnt" 
                  size="mini" 
                  placeholder="数量"
                  @blur="validateCnt(scope.row)"
                  style="width:70px;"
                ></el-input>
                <span style="color:#909399; font-size:12px; white-space:nowrap;">{{ scope.row.unitLabel || '' }}</span>
              </div>
            </template>
          </el-table-column>

          <!-- 开单数量列：直接显示 quantity，不乘 pacCnt -->
          <el-table-column label="开单数量" width="130" align="center">
            <template slot-scope="scope">
              <div v-if="scope.row.manuLotnum && scope.row.calcPresQty !== undefined">
                <span>{{ scope.row.calcPresQty }}</span>
                <span style="color:#909399; font-size:12px; margin-left:4px;">{{ scope.row.unitName || '' }}</span>
              </div>
            </template>
          </el-table-column>

          <!-- 包装数量列 -->
          <el-table-column label="包装数量" width="80" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.pacCnt">{{ scope.row.pacCnt }}</span>
            </template>
          </el-table-column>

          <el-table-column label="批次号" width="180" align="center">
            <template slot-scope="scope">
              <el-select 
                v-model="scope.row.manuLotnum" 
                placeholder="请选择批次"
                size="mini"
                style="width: 100%"
                @visible-change="(val) => handleBatchDropDown(val, scope.row)"
                @change="handleBatchChange(scope.row)"
              >
                <el-option
                  v-for="item in batchOptions"
                  :key="item.manuLotnum"
                  :label="item.manuLotnum"
                  :value="item.manuLotnum"
                >
                  <div style="display: flex; justify-content: space-between; flex-wrap: nowrap;">
                    <span style="flex: 0 0 120px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">批次：{{ item.manuLotnum }}</span>
                    <span style="flex: 1; margin: 0 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ batchCurrentItemName }}</span>
                    <span style="flex: 0 0 120px; margin-right: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">文号：{{ item.approvalNumber }}</span>
                    <span style="flex: 0 0 120px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">厂商：{{ item.prodentpName }}</span>
                  </div>
                </el-option>
              </el-select>
            </template>
          </el-table-column>
          
          <el-table-column label="实际库存数量(小)" width="140" align="center">
            <template slot-scope="scope">{{ scope.row.purcCnt || '-' }}</template>
          </el-table-column>
          <el-table-column prop="approvalNumber" label="批准文号" width="140" show-overflow-tooltip></el-table-column>
          <el-table-column prop="prodentpName" label="生产厂商" min-width="150" show-overflow-tooltip></el-table-column>
          
          <el-table-column label="操作" width="80" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="text" icon="el-icon-delete" @click="removeBatchRow(scope.$index)">移除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="batchDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="confirmBatchSelect">确认批次</el-button>
      </span>
    </el-dialog>

    <!-- 追溯码采集模态框 -->
    <el-dialog
      :title="traceCurrentItemName + ' - 进行发药追溯码采集'"
      :visible.sync="traceDialogVisible"
      width="900px"
      :close-on-click-modal="false"
      custom-class="trace-dialog"
    >
      <div class="trace-dialog-content" v-loading="traceLoading" element-loading-text="正在查询核验当前追溯码(已录入的追溯码)...">
        <el-table
          :data="traceTableData"
          border
          style="width: 100%"
          height="350"
        >
          <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
          <el-table-column label="追溯码" width="160" align="center">
            <template slot-scope="scope">
              <el-input 
                v-model="scope.row.code" 
                size="mini" 
                placeholder="录入追溯码"
                @blur="handleTraceBlur(scope.row)"
              ></el-input>
            </template>
          </el-table-column>
          <el-table-column prop="drugName" label="药品名称" min-width="120" show-overflow-tooltip></el-table-column>
          <el-table-column prop="spec" label="规格" width="100" show-overflow-tooltip></el-table-column>
          <el-table-column label="可用数量" width="100" align="center">
            <template slot-scope="scope">
              <el-input 
                v-model="scope.row.cnt" 
                size="mini" 
                placeholder="数量"
                @blur="validateCnt(scope.row)"
              ></el-input>
            </template>
          </el-table-column>
          <el-table-column prop="batchNo" label="批次号" width="140"></el-table-column>
          <el-table-column prop="approvalNumber" label="批准文号" width="140" show-overflow-tooltip></el-table-column>
          <el-table-column prop="produceDateStr" label="生产期" width="120"></el-table-column>
          <el-table-column prop="originalExpireDate" label="有效期" width="120"></el-table-column>
          <el-table-column prop="prodentpName" label="生产厂商" min-width="150" show-overflow-tooltip></el-table-column>
          <el-table-column label="操作" width="80" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="text" icon="el-icon-delete" @click="removeTraceRow(scope.$index)">移除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="traceDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="confirmTraceCode" :disabled="traceTableData.filter(r => r.code).length === 0">确认追溯码</el-button>
      </span>
    </el-dialog>

    <!-- 步骤进度条模态框 -->
    <el-dialog
      title="发药进度"
      :visible.sync="processDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      custom-class="process-dialog"
      :close-on-press-escape="false"
    >
      <div class="process-content" style="padding: 4px 0;">
        <el-steps :active="processStep" direction="vertical">
          <el-step 
            title="上传追溯码至码上放心平台" 
            :description="processLogs[0] || '准备上传...'"
            :status="processStep > 0 ? (processStatus === 'msfx_error' ? 'error' : 'success') : 'process'">
          </el-step>
          <el-step 
            title="执行发药操作" 
            :description="processLogs[1] || '等待发药...'"
            :status="processStep > 1 ? (processStatus === 'fy_error' ? 'error' : 'success') : 'wait'">
          </el-step>
          <el-step 
            title="完成" 
            :description="processLogs[2] || '请稍后...'"
            :status="processStep > 2 ? 'success' : 'wait'">
          </el-step>
        </el-steps>
        
        <div v-if="processStatus === 'msfx_error'" class="error-msg" style="margin-top: 12px; color: #f56c6c; border: 1px solid #fde2e2; background: #fef0f0; padding: 8px 12px; border-radius: 4px; font-size: 13px;">
          <i class="el-icon-warning" style="margin-right: 4px;"></i>
          注意：追溯码上传失败，但发药操作仍在继续，请检查追溯码上传记录。
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleCloseProcessDialog" v-if="processStep >= 2 || processStatus === 'msfx_error' || processStatus === 'fy_error'">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
// 引入接口
import { selectPatientInfoV2, getPatientInfoV2, fySaveV2, outpTable } from "@/api/MedicenHouse/WesternGive.js";
import { getDInfos } from "@/api/MedicenCata.js";
import { getTraMedic } from "@/api/systemCatalogue/traMedici.js";
import { detailEisai } from "@/api/Outpatient/eisai.js";
import { aiseDetails } from "@/api/reservation/healthrecords.js";
import { getStaff } from "@/api/user.js";
import { getDeptAttribute } from "@/api/ks/ks.js";
import { getSystemList } from "@/api/systemParameter/systemParameter";
import { msfxCodeDetail, msfxUploadRetail, getSomeWare } from "@/api/systemCatalogue/Western.js";
// ★★★ 新增字典接口 ★★★
import { dict } from "@/api/staff/staff.js";

export default {
  name: 'GiveMedicine',
  data() {
    return {
      loading: false,
      searchKeyword: '',
      searchDate: '',
      pageNum: 1,
      pageSize: 20,
      totalCount: 0,
      patientList: [],
      currentPatient: null,
      
      prescriptionList: [],
      activePrescriptionIndex: -1,
      activePrescriptionItems: [],
      activePrescriptionTotal: '0.00',
      
      userInfo: {},
      dialogVisible: false,
      loadingDrug: false,
      drugDetail: {},

      clickCount: 0,
      clickTimer: null,

      needMaShangFangXin: false,
      needStockBatchSelect: false,

      // ★★★ 新增字典缓存 ★★★
      routeDict: {},     // 给药方式字典 { id: name }
      frequencyDict: {}, // 频次字典 { id: name }

      batchDialogVisible: false,
      batchList: [],
      batchOptions: [],
      batchCurrentOrderItemId: '',
      batchCurrentOrderItem: null,
      batchCurrentItemName: '',
      batchCurrentItemTypeName: '',
      batchSelectedIndex: -1,

      traceDialogVisible: false,
      traceCurrentItemName: '',
      traceCurrentOrderItemId: '',
      traceInput: '',
      traceResult: {},
      traceLoading: false,
      traceTableData: [],

      processDialogVisible: false,
      processStep: 0,
      processLogs: [],
      processStatus: 'pending',
      isGiveSuccess: false,
    }
  },
  computed: {
    totalDrugCount() {
      if (!this.activePrescriptionItems) return 0;
      return this.activePrescriptionItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    },
    allItemsCount() {
      if (!this.prescriptionList || this.prescriptionList.length === 0) return 0;
      let count = 0;
      this.prescriptionList.forEach(p => {
        if (p.items && Array.isArray(p.items)) {
          count += p.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        }
      });
      return count;
    },
    isGiveMedicineDisabled() {
      if (!this.activePrescriptionItems || this.activePrescriptionItems.length === 0) return true;
      for (let item of this.activePrescriptionItems) {
        if (item.itemType != 1) continue;
        if (item.isBar === 1) {
          if (!item.tracCodeJson) return true;
          try {
            const parsed = JSON.parse(item.tracCodeJson);
            let hasBarcode = false;
            for (let p of parsed) {
              if (p.barcode && p.barcode !== '') {
                hasBarcode = true;
                break;
              }
            }
            if (!hasBarcode) return true;
          } catch (e) {
            return true;
          }
        } else if (this.needStockBatchSelect) {
          if (!item.selectedBatchNum) return true;
        }
      }
      return false;
    }
  },
  created() {
    this.getUserInfo();
    this.fetchPatients();
  },
  methods: {
    getUserInfo() {
      const info = localStorage.getItem('userInfo');
      if (info) {
        this.userInfo = JSON.parse(info);
      }
    },

    clearSearch() {
      this.searchKeyword = '';
      this.searchDate = '';
      this.fetchPatients();
    },

    async fetchPatients() {
      this.loading = true;
      this.currentPatient = null;
      this.prescriptionList = [];
      this.activePrescriptionIndex = -1;
      this.activePrescriptionItems = [];
      this.activePrescriptionTotal = '0.00';

      try {
        const params = { patientName: this.searchKeyword || '', patientId: '' };
        if (this.searchDate) params.visitDate = this.formatDate(this.searchDate);
        const res = await selectPatientInfoV2(params);
        if (res.data && res.data.content) {
          this.patientList = res.data.content;
          this.totalCount = res.data.totalSize || this.patientList.length;
          if (this.patientList.length > 0) this.selectPatient(this.patientList[0]);
          else this.currentPatient = null;
        }

        // ★★★ 获取字典数据（给药方式、频次）★★★
        try {
          const dictRes = await dict();
          if (dictRes && dictRes.data) {
            // 处理给药方式
            if (dictRes.data.ruteVo && Array.isArray(dictRes.data.ruteVo)) {
              this.routeDict = {};
              dictRes.data.ruteVo.forEach(item => {
                if (item.id !== undefined && item.name) {
                  this.routeDict[item.id] = item.name;
                }
              });
            }
            // 处理频次
            if (dictRes.data.tcmway && Array.isArray(dictRes.data.tcmway)) {
              this.frequencyDict = {};
              dictRes.data.tcmway.forEach(item => {
                if (item.id !== undefined && item.name) {
                  this.frequencyDict[item.id] = item.name;
                }
              });
            }
          }
        } catch (dictError) {
          console.error('获取字典数据失败', dictError);
          // 字典加载失败不影响主要功能，仅显示ID
        }
      } catch (error) {
        console.error('获取患者列表失败', error);
        this.$message.error('获取患者列表失败');
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    async selectPatient(patient) {
      if (this.clickCount >= 3) {
        this.$alert('您点击的太快了，请于10S后再试！', '系统提示', { confirmButtonText: '确定', type: 'warning' });
        return;
      }
      this.clickCount++;
      clearTimeout(this.clickTimer);
      this.clickTimer = setTimeout(() => { this.clickCount = 0; }, 10000);

      if (!patient || !patient.patientId) return;
      
      this.currentPatient = patient;
      this.prescriptionList = [];
      this.activePrescriptionIndex = -1;
      this.activePrescriptionItems = [];
      this.activePrescriptionTotal = '0.00';

      try {
        this.loading = true;
        const res = await getPatientInfoV2({ patientId: patient.patientId });
        if (!res.data || res.data.length === 0) {
          this.$message.warning('未获取到该患者的详细数据');
          return;
        }
        
        // ★★★ 转换处方明细中的用法和频次 ★★★
        const transformedData = res.data.map(pres => {
          // 处理医生名称（保留原有逻辑）
          // 转换 items
          if (pres.items && Array.isArray(pres.items)) {
            pres.items = pres.items.map(item => {
              // 根据 routeId 查找给药方式名称
              const routeName = this.routeDict[item.routeId] || item.routeId || '';
              // 根据 frequencyId 查找频次名称
              const frequencyName = this.frequencyDict[item.frequencyId] || item.frequencyId || '';
              return {
                ...item,
                routeName: routeName,
                frequencyName: frequencyName
              };
            });
          }
          return pres;
        });

        // 更新医生名称（原有逻辑）
        const updatePromises = transformedData.map(async (pres) => {
          if (pres.orderingDoctorId) {
            try {
              const staffRes = await getStaff({ id: pres.orderingDoctorId });
              if (staffRes.data) pres.doctorName = staffRes.data.fullName;
            } catch (e) { console.error('获取医生信息失败', e); }
          }
          return pres;
        });
        await Promise.all(updatePromises);

        this.prescriptionList = transformedData;
        const baseInfo = transformedData[0];
        const idx = this.patientList.findIndex(p => p.patientId === patient.patientId);
        if (idx !== -1) this.patientList[idx].rxCount = transformedData.length;

        try {
          const sysRes = await getSystemList();
          if (sysRes.data && Array.isArray(sysRes.data)) {
            const maShang = sysRes.data.find(item => item.name && (item.name.includes('码上放心') || item.name.includes('马上放心开关')) && item.value == 1);
            this.needMaShangFangXin = !!maShang;
            const batchFlag = sysRes.data.find(item => item.name && item.name.includes('库存批次选择开关') && item.value == 1);
            this.needStockBatchSelect = !!batchFlag;
          }
        } catch (e) { console.error('获取系统参数失败', e); }

        let finalPatient = { ...this.currentPatient, ...baseInfo };
        try {
          const detailRes = await aiseDetails({ id: patient.patientId });
          if (detailRes.data) {
            finalPatient.healthId = detailRes.data.patId || '-';
            finalPatient.age = detailRes.data.age || '-';
            finalPatient.sex = detailRes.data.sex;
            finalPatient.tel = detailRes.data.tel || '-';
          }
        } catch (e) { console.error('获取患者详情失败', e); }

        if (baseInfo.orderingDoctorId) {
          try {
            const staffRes = await getStaff({ id: baseInfo.orderingDoctorId });
            if (staffRes.data) finalPatient.doctorName = staffRes.data.fullName || baseInfo.orderingDoctorId;
          } catch (e) { console.error('获取医生信息失败', e); }
        }
        if (baseInfo.orderingDeptId) {
          try {
            const deptRes = await getDeptAttribute({ deptId: baseInfo.orderingDeptId });
            if (deptRes.data) finalPatient.deptName = deptRes.data.deptName || baseInfo.orderingDeptId;
          } catch (e) { console.error('获取科室信息失败', e); }
        }

        finalPatient.orderedAt = baseInfo.orderedAt || '-';
        finalPatient.orderNo = baseInfo.orderNo || '-';
        finalPatient.orderNoType = baseInfo.orderNoType || '-';
        finalPatient.icdName = baseInfo.icdName || '-';
        finalPatient.remark = baseInfo.remark || '无特殊禁忌';
        finalPatient.totalAmount = baseInfo.totalAmount || 0;

        this.currentPatient = finalPatient;
        this.selectPrescription(0);
      } catch (error) {
        console.error('获取患者详细信息失败', error);
        this.$message.error('获取患者详细信息失败');
      } finally {
        this.loading = false;
      }
    },

    selectPrescription(index) {
      if (index < 0 || index >= this.prescriptionList.length) return;
      this.activePrescriptionIndex = index;
      const selected = this.prescriptionList[index];
      this.activePrescriptionItems = selected.items || [];
      this.activePrescriptionTotal = (selected.totalAmount || 0).toFixed(2);
      if (this.currentPatient) {
        this.currentPatient.orderNo = selected.orderNo || '-';
        this.currentPatient.orderNoType = selected.orderNoType || '-';
        this.currentPatient.orderedAt = selected.orderedAt || '-';
        this.currentPatient.icdName = selected.icdName || '-';
        this.currentPatient.remark = selected.remark || '无特殊禁忌';
        this.currentPatient.totalAmount = selected.totalAmount || 0;
      }
    },

    formatItemType(itemType) {
      const map = { 1: '西药', 2: '中药', 4: '理疗', 5: '卫材', 6: '输液/治疗', 7: '检查', 8: '检验', 9: '挂号费', 10: '其他' };
      return map[itemType] || '其他';
    },
    getItemTypeTagType(itemType) {
      const map = { 1: 'success', 2: 'primary', 4: 'info', 5: 'warning', 6: 'danger', 7: 'primary', 8: 'success', 9: 'info', 10: 'info' };
      return map[itemType] || 'info';
    },

    // ---------- 批次选择相关 ----------
    async handleBatchSelect(row) {
      this.batchCurrentOrderItemId = row.id;
      this.batchCurrentOrderItem = row;
      this.batchCurrentItemName = row.itemName;
      this.batchCurrentItemTypeName = this.formatItemType(row.itemType);
      this.batchOptions = [];
      
      if (row.cachedBatchRows && row.cachedBatchRows.length > 0) {
        this.batchList = JSON.parse(JSON.stringify(row.cachedBatchRows));
        this.batchList.push({ isNew: true });
      } else {
        this.batchList = [{ isNew: true }];
      }
      
      this.batchDialogVisible = true;
    },

    async handleBatchDropDown(val, row) {
      if (val && this.batchOptions.length === 0) {
        try {
          const res = await outpTable({ orderItemId: this.batchCurrentOrderItemId });
          if (res.data && Array.isArray(res.data)) {
            this.batchOptions = res.data;
          }
        } catch (error) {
          console.error('获取批次列表失败', error);
          this.$message.error('获取批次列表失败');
        }
      }
    },

    handleBatchChange(row) {
      const selected = this.batchOptions.find(item => item.manuLotnum === row.manuLotnum);
      if (selected) {
        this.$set(row, 'purcCnt', selected.purcCnt);
        this.$set(row, 'approvalNumber', selected.approvalNumber);
        this.$set(row, 'prodentpName', selected.prodentpName);
        this.$set(row, 'manuLotnum', selected.manuLotnum);
        this.$set(row, 'tableId', selected.tableId);
        this.$set(row, 'pacCnt', selected.pacCnt || 1);
        this.$set(row, 'minUntId', selected.minUntId);
        this.$set(row, 'pacUntId', selected.pacUntId);
        this.$set(row, 'minUntName', selected.minUntName || '');
        this.$set(row, 'pacUntName', selected.pacUntName || '');
        this.$set(row, 'chgCnt', '');
        
        if (this.batchCurrentOrderItem) {
          const unitId = this.batchCurrentOrderItem.unitId;
          const pacUntId = selected.pacUntId;
          const minUntId = selected.minUntId;
          let calcQty = 0;
          let unitName = '';
          let unitLabel = '';
          
          calcQty = parseInt(this.batchCurrentOrderItem.quantity);
          
          if (unitId === pacUntId) {
            unitName = selected.pacUntName || '';
            unitLabel = selected.pacUntName || '';
          } else {
            unitName = selected.minUntName || '';
            unitLabel = selected.minUntName || '';
          }
          this.$set(row, 'calcPresQty', calcQty);
          this.$set(row, 'unitName', unitName);
          this.$set(row, 'unitLabel', unitLabel);
        }

        const lastRow = this.batchList[this.batchList.length - 1];
        if (row === lastRow || this.batchList[this.batchList.length - 1].isNew) {
          this.batchList.push({ isNew: true });
        }
      }
    },

    validateCnt(row) {
      if (!row.manuLotnum) {
        this.$message.warning('请先选择批次号');
        this.$set(row, 'chgCnt', '');
        return;
      }
      
      let val = String(row.chgCnt || '').replace(/[^0-9]/g, '').replace(/^0+/, '');
      if (val === '') val = '0';
      const chgNum = parseInt(val);
      const purcNum = parseInt(row.purcCnt);
      
      if (chgNum <= 0) {
        this.$message.warning('发药数量必须为大于0的正整数');
        this.$set(row, 'chgCnt', '');
        return;
      }
      if (chgNum > purcNum) {
        this.$message.warning('发药数量不能大于实际库存数量（' + purcNum + '）');
        this.$set(row, 'chgCnt', '');
        return;
      }
      this.$set(row, 'chgCnt', val);
    },

    removeBatchRow(index) {
      this.batchList.splice(index, 1);
      if (this.batchList.length === 0) {
        this.batchList.push({ isNew: true });
      }
    },

    confirmBatchSelect() {
      const validRows = this.batchList.filter(row => row.manuLotnum && row.chgCnt && parseInt(row.chgCnt) > 0);
      
      if (validRows.length === 0) {
        this.$message.warning('请至少选择并填写一个有效批次及发药数量');
        return;
      }
      
      let totalChg = 0;
      validRows.forEach(row => {
        totalChg += parseInt(row.chgCnt);
      });

      const firstValidRow = validRows[0];
      const calcPresQty = firstValidRow && firstValidRow.calcPresQty !== undefined ? parseInt(firstValidRow.calcPresQty) : 0;

      if (calcPresQty > 0) {
        if (totalChg !== calcPresQty) {
          this.$alert('当前所有批次发药数量之和与订单数量不一致！请检查确保发药数量与订单数量一致，再进行发药操作。', '系统提示', {
            confirmButtonText: '确定',
            type: 'warning'
          });
          return;
        }
      } else {
        this.$message.warning('无法获取开单数量信息，请重新选择药品');
        return;
      }
      
      const targetRow = this.activePrescriptionItems.find(item => item.id === this.batchCurrentOrderItemId);
      if (targetRow) {
        const unitId = targetRow.unitId;
        const batchJson = validRows.map(row => {
          let chgCntForBackend = 0;
          if (unitId === row.pacUntId) {
            chgCntForBackend = parseInt(row.chgCnt) * parseInt(row.pacCnt || 1);
          } else {
            chgCntForBackend = parseInt(row.chgCnt);
          }
          return {
            barcode: '',
            chgCnt: chgCntForBackend,
            manuLotnum: row.manuLotnum,
            tableId: row.tableId
          };
        });
        const jsonStr = JSON.stringify(batchJson);
        
        this.$set(targetRow, 'tracCodeJson', jsonStr);
        this.$set(targetRow, 'selectedBatchNum', `已选批次（${validRows.length}）`);
        this.$set(targetRow, 'cachedBatchRows', JSON.parse(JSON.stringify(validRows)));
      }

      this.batchDialogVisible = false;
      this.$message.success('批次选择成功');
    },

    // ---------- 追溯码采集相关 ----------
    handleTraceCode(row) {
      this.traceCurrentItemName = row.itemName;
      this.traceCurrentOrderItemId = row.id;
      
      if (row.cachedTraceTableData && row.cachedTraceTableData.length > 0) {
        this.traceTableData = JSON.parse(JSON.stringify(row.cachedTraceTableData));
      } else {
        this.traceTableData = [{ code: '' }];
      }
      this.traceDialogVisible = true;
    },

    async handleTraceBlur(row) {
      if (!row.code) {
        return;
      }
      
      const exist = this.traceTableData.some(item => item.code === row.code && item !== row);
      if (exist) {
        this.$alert('当前的追溯码已录入，请勿重复录入！', '系统提示', {
          confirmButtonText: '确定',
          type: 'warning'
        });
        this.$set(row, 'code', '');
        return;
      }

      this.traceLoading = true;
      try {
        const detailRes = await msfxCodeDetail({ codes: row.code });
        if (detailRes.code === 200 && detailRes.data && detailRes.data.length > 0) {
          const data = detailRes.data[0];
          const traceBatchNo = data.codeProduceInfoDTO?.produceInfoList?.[0]?.batchNo || '';
          const traceApprovalNo = data.drugEntBaseDTO?.approvalLicenceNo || '';

          const batchRes = await outpTable({ orderItemId: this.traceCurrentOrderItemId });
          let matchedBatch = null;
          if (batchRes.data && Array.isArray(batchRes.data)) {
            matchedBatch = batchRes.data.find(item => 
              item.approvalNumber === traceApprovalNo && 
              item.manuLotnum === traceBatchNo
            );
          }

          if (!matchedBatch) {
            this.$alert('当前库房药品信息中不存在当前追溯码药品信息，请检查！', '系统提示', {
              confirmButtonText: '确定',
              type: 'error'
            });
            this.$set(row, 'code', '');
            this.$set(row, 'drugName', '');
            this.$set(row, 'spec', '');
            this.$set(row, 'cnt', '');
            this.$set(row, 'batchNo', '');
            this.$set(row, 'approvalNumber', '');
            this.$set(row, 'produceDateStr', '');
            this.$set(row, 'originalExpireDate', '');
            this.$set(row, 'prodentpName', '');
            return;
          }

          this.$set(row, 'drugName', data.drugEntBaseDTO?.physicName || '-');
          this.$set(row, 'spec', data.drugEntBaseDTO?.pkgSpecCrit || '-');
          this.$set(row, 'cnt', data.drugEntBaseDTO?.prepnSpec || 1);
          this.$set(row, 'batchNo', traceBatchNo);
          this.$set(row, 'approvalNumber', traceApprovalNo);
          this.$set(row, 'produceDateStr', data.codeProduceInfoDTO?.produceInfoList?.[0]?.produceDateStr || '-');
          this.$set(row, 'originalExpireDate', data.codeProduceInfoDTO?.produceInfoList?.[0]?.originalExpireDate || '-');
          this.$set(row, 'prodentpName', data.pUserEntDTO?.entName || '-');
          this.$set(row, 'tableId', matchedBatch.tableId);
          
          this.traceTableData.push({ code: '' });
        } else {
          this.$message.warning('未查询到追溯码信息');
          this.$set(row, 'code', '');
        }
      } catch (error) {
        console.error('追溯码查询失败', error);
        this.$message.error('追溯码查询失败');
        this.$set(row, 'code', '');
      } finally {
        this.traceLoading = false;
      }
    },

    removeTraceRow(index) {
      this.traceTableData.splice(index, 1);
      if (this.traceTableData.length === 0) {
        this.traceTableData.push({ code: '' });
      }
    },

    confirmTraceCode() {
      const validRows = this.traceTableData.filter(row => row.code && row.cnt && parseInt(row.cnt) > 0);
      if (validRows.length === 0) {
        this.$message.warning('请至少录入一个有效追溯码');
        return;
      }
      const targetRow = this.activePrescriptionItems.find(item => item.id === this.traceCurrentOrderItemId);
      if (targetRow) {
        const traceJsonArr = validRows.map(item => ({
          barcode: item.code,
          chgCnt: parseInt(item.cnt),
          manuLotnum: item.batchNo,
          tableId: item.tableId || ''
        }));
        const jsonStr = JSON.stringify(traceJsonArr);
        
        this.$set(targetRow, 'tracCodeJson', jsonStr);
        this.$set(targetRow, 'traceCodeCount', validRows.length);
        this.$set(targetRow, 'traceDisplayText', `追溯码采集追溯码（${validRows.length}）`);
        this.$set(targetRow, 'cachedTraceTableData', JSON.parse(JSON.stringify(this.traceTableData)));
      }
      this.traceDialogVisible = false;
      this.$message.success('追溯码录入成功');
    },

    // ---------- 关闭进度条模态框的处理方法 ----------
    handleCloseProcessDialog() {
      if (this.isGiveSuccess && this.processStatus === 'msfx_error') {
        this.processDialogVisible = false;
        this.$message.info('追溯码上传失败，但发药已完成，正在刷新数据...');
        this.fetchPatients();
      } else {
        this.processDialogVisible = false;
      }
      this.isGiveSuccess = false;
      this.processStatus = 'pending';
    },

    // ---------- 发药操作相关 ----------
    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },

    async handleGiveMedicine() {
      if (this.isGiveMedicineDisabled) {
        this.$message.warning('请先完成所有药品的批次选择和追溯码采集');
        return;
      }
      const selectedPres = this.prescriptionList[this.activePrescriptionIndex];
      if (!selectedPres) {
        this.$message.warning('请先选择处方');
        return;
      }

      this.processDialogVisible = true;
      this.processStep = 0;
      this.processLogs = [];
      this.processStatus = 'pending';
      this.isGiveSuccess = false;

      let msfxError = false;

      let traceCodes = [];
      this.activePrescriptionItems.forEach(item => {
        if (item.tracCodeJson) {
          try {
            const parsed = JSON.parse(item.tracCodeJson);
            if (Array.isArray(parsed)) {
              parsed.forEach(p => {
                if (p.barcode) {
                  traceCodes.push(p.barcode);
                }
              });
            }
          } catch (e) {}
        }
      });

      const hasTraceCodes = traceCodes.length > 0;

      try {
        if (hasTraceCodes) {
          this.processLogs[0] = '正在上传追溯码至码上放心平台...';
          const params = {
            billType: 321,
            bizNum: selectedPres.orderNo,
            traceCodes: traceCodes.join(',')
          };
          const traceRes = await msfxUploadRetail(params);
          if (traceRes.code !== 200) {
            this.processLogs[0] = `追溯码上传失败：${traceRes.message || '未知错误'}`;
            this.processStatus = 'msfx_error';
            msfxError = true;
          } else {
            this.processLogs[0] = '追溯码上传成功！';
          }
        } else {
          this.processLogs[0] = '无追溯码需要上传，跳过该步骤';
        }
      } catch (error) {
        this.processLogs[0] = `追溯码上传发生异常：${error.message}`;
        this.processStatus = 'msfx_error';
        msfxError = true;
      }
      this.processStep = 1;

      try {
        this.processLogs[1] = '正在执行发药操作...';
        const orderId = selectedPres.id;
        const list = this.activePrescriptionItems.map(item => ({
          orderItemId: item.id,
          tracCodeJson: item.tracCodeJson || ''
        }));
        const requestNo = this.generateUUID();
        
        const payload = {
          list: list,
          orderId: orderId,
          requestNo: requestNo
        };

        const fyRes = await fySaveV2(payload);
        if (fyRes.code === 200) {
          this.processLogs[1] = '发药操作成功！';
          this.isGiveSuccess = true;
          this.processStep = 2;
          this.processLogs[2] = '流程完成，发药已结束。';
          
          if (!msfxError) {
            setTimeout(() => {
              this.processDialogVisible = false;
              this.$message.success('发药成功');
              this.fetchPatients();
            }, 1500);
          } else {
            this.processStatus = 'msfx_error';
            this.processLogs[2] = '流程结束，追溯码上传存在异常，请后续进行补传。';
          }
        } else {
          this.processLogs[1] = `发药操作失败：${fyRes.message || '未知错误'}`;
          this.processStatus = 'fy_error';
          this.processLogs[2] = '流程异常中止，请检查日志。';
          this.isGiveSuccess = false;
        }
      } catch (error) {
        this.processLogs[1] = `发药操作发生异常：${error.message}`;
        this.processStatus = 'fy_error';
        this.processLogs[2] = '流程异常中止，请检查日志。';
        this.isGiveSuccess = false;
      }
    },

    copyAllDrugNames() {
      if (!this.activePrescriptionItems || this.activePrescriptionItems.length === 0) {
        this.$message.warning('暂无药品可复制');
        return;
      }
      const names = this.activePrescriptionItems.map(item => item.itemName).join('\n');
      this.copyText(names);
    },

    copyText(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => this.$message.success('复制成功'));
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.$message.success('复制成功');
      }
    }
  }
}
</script>

<style lang="less" scoped>
/* ===== 全局容器 ===== */
.give-medicine-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  overflow: hidden;
  background: #f5f7fa;
  font-family: '等线', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  padding: 12px 16px;
  box-sizing: border-box;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
}

/* ===== 基础颜色/样式辅助 ===== */
.text-primary { color: #409eff; }
.text-red { color: #f56c6c; }
.text-muted { color: #909399; }
.ml-5 { margin-left: 5px; }

/* ===== 顶部区域 ===== */
.top-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
  margin-bottom: 12px;
}

.search-panel {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  .search-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    .label { font-size: 14px; color: #303133; font-weight: 500; }
    .dimension-group {
      display: flex; align-items: center; border: 1px solid #dcdfe6; border-radius: 4px; background: #fff; height: 32px; padding: 0 4px;
      .dimension-item {
        padding: 4px 10px; font-size: 12px; color: #606266; cursor: pointer; display: flex; align-items: center; gap: 4px;
        &:hover { color: #409eff; }
      }
    }
  }

  .search-middle { flex: 1; .search-input { width: 100%; } }

  .search-right {
    display: flex; gap: 8px; flex-shrink: 0;
    .btn-query { background-color: #409eff; border-color: #409eff; color: #fff; }
  }
}

.patient-card {
  border-radius: 6px;
  &.empty { display: flex; align-items: center; justify-content: center; color: #909399; font-size: 14px; height: 60px; }
  .card-row {
    display: flex; align-items: center; flex-wrap: wrap; gap: 6px 20px; background: #eaedf2; padding: 16px; margin-bottom: 8px;
    &:last-child { margin-bottom: 0; }
    .card-item {
      font-size: 13px; display: flex; align-items: center;
      .label { color: #909399; margin-right: 4px; white-space: nowrap; }
      .val { color: #303133; font-weight: 500; white-space: nowrap; }
    }
  }
}

/* ===== 主体双栏区域 ===== */
.main-body { flex: 1; display: flex; gap: 12px; overflow: hidden; min-height: 0; }

/* ===== 左侧列表区 ===== */
.left-panels {
  width: 360px; min-width: 360px; display: flex; flex-direction: column; gap: 12px; overflow: hidden; flex-shrink: 0;
  .list-panel {
    background: #fff; border-radius: 6px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04); display: flex; flex-direction: column; overflow: hidden;
    .panel-title { font-size: 15px; font-weight: 700; color: #303133; padding: 12px 14px; border-bottom: 1px solid #ebeef5; flex-shrink: 0; }
    .list-scroll { flex: 1; overflow-y: auto; padding: 4px 0; &::-webkit-scrollbar { width: 4px; } &::-webkit-scrollbar-thumb { background: #dcdfe6; border-radius: 2px; } }
    .empty-text { display: flex; align-items: center; justify-content: center; color: #909399; font-size: 12px; padding: 20px 0; }
    .list-item {
      display: flex; align-items: center; padding: 8px 14px; cursor: pointer; transition: all 0.2s ease; border-left: 3px solid transparent;
      &:hover { background: #f5f7fa; }
      &.active { background: #ecf5ff; border-left-color: #409eff; .item-info .name { color: #409eff; font-weight: 600; } }
    }
  }

  .top-list {
    flex: 1.3; max-height: 160px;
    .list-item .item-info {
      display: flex; align-items: center; gap: 6px; font-size: 13px;
      .blue-square { display: inline-block; width: 6px; height: 6px; background: #409eff; border-radius: 2px; margin-right: 4px; }
      .name { color: #303133; }
      .gender.male { color: #409eff; }
      .gender.female { color: #f56c6c; }
      .age { color: #909399; }
      .desc { color: #606266; margin-left: 6px; }
    }
  }

  .bottom-list {
    flex: 1;
    .pres-table-header {
      display: flex; padding: 6px 14px; background: #f8f9fa; border-bottom: 1px solid #ebeef5; font-size: 12px; color: #909399;
      .th { flex: 1; text-align: left; &:nth-child(1) { flex: 0 0 40px; text-align: center; } &:nth-child(2) { flex: 1; } &:nth-child(3) { flex: 0 0 80px; text-align: right;} &:nth-child(4) { flex: 0 0 70px; text-align: right;} }
    }
    .list-item.pres-item {
      display: flex; padding: 8px 14px;
      .pres-col { font-size: 13px; color: #606266; &:nth-child(1) { flex: 0 0 40px; text-align: center; color: #303133;} &:nth-child(2) { flex: 1; color: #303133; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; } &:nth-child(3) { flex: 0 0 80px; text-align: right; color: #303133;} &:nth-child(4) { flex: 0 0 70px; text-align: right; color: #303133;} }
    }
  }
}

/* ===== 右侧明细区 ===== */
.right-panel {
  flex: 1; background: #fff; border-radius: 6px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04); display: flex; flex-direction: column; overflow: hidden;
  .right-header { font-size: 15px; font-weight: 700; color: #303133; padding: 12px 14px; border-bottom: 1px solid #ebeef5; flex-shrink: 0; }
  .table-wrapper { flex: 1; overflow: hidden; padding: 0 14px; /deep/ .el-table { height: 100%; .el-table__body-wrapper { overflow-y: auto; } } /deep/ .el-table th { background-color: #f8f9fa; color: #606266; font-weight: 600; } }
  .op-action-group { display: flex; align-items: center; justify-content: center; gap: 8px; flex-wrap: nowrap; white-space: nowrap; }
  .right-footer {
    flex-shrink: 0; padding: 12px 14px; border-top: 1px solid #ebeef5; display: flex; align-items: center; justify-content: space-between; background: #fafbfc;
    .footer-stats { display: flex; align-items: center; gap: 24px; font-size: 14px; color: #303133; .stat-val { font-weight: 600; margin-left: 4px; } }
    .footer-btns { display: flex; gap: 12px; }
  }
}

/* ===== 追溯码模态框列表滚动区 ===== */
.trace-list-wrapper {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px;
}
.trace-list-wrapper::-webkit-scrollbar { width: 4px; }
.trace-list-wrapper::-webkit-scrollbar-thumb { background: #dcdfe6; border-radius: 2px; }

/* ===== 项目详情模态框 ===== */
.drug-detail-content { .detail-row { padding: 8px 0; border-bottom: 1px solid #ebeef5; &:last-child { border-bottom: none; } .label { color: #909399; display: inline-block; width: 80px; } } }

.info-row { display: flex; align-items: center; padding: 6px 0; .label { color: #909399; width: 80px; flex-shrink: 0; } .el-input { width: 80px !important; } }

/* ===== 批次与追溯码模态框通用样式 ===== */
::v-deep .batch-dialog .el-dialog__header, ::v-deep .trace-dialog .el-dialog__header { background-color: #2c4f79 !important; padding: 12px 20px; border-bottom: 1px solid #1f3a57; border-radius: 8px 8px 0 0; }
::v-deep .batch-dialog .el-dialog__title, ::v-deep .trace-dialog .el-dialog__title { color: #ffffff !important; font-size: 16px; font-weight: 600; }
::v-deep .batch-dialog .el-dialog__headerbtn .el-dialog__close, ::v-deep .trace-dialog .el-dialog__headerbtn .el-dialog__close { color: #ffffff !important; &:hover { color: #e6e6e6 !important; } }

/* ===== 批次选择模态框专属 ===== */
::v-deep .batch-dialog .el-dialog__body { padding: 0 20px 20px 20px !important; }
.batch-dialog-content { padding: 4px 0; }

/* ===== 追溯码模态框专属 ===== */
::v-deep .trace-dialog .el-dialog__body { padding: 20px 20px; }
.trace-dialog-content { .el-collapse-item__content { padding-bottom: 10px; } }
.trace-input-wrapper { margin-bottom: 4px; .el-input { width: 100%; } }
.empty-tip-small { padding: 20px 0; color: #909399; text-align: center; }

/* ===== 进度条模态框专属样式 ===== */
::v-deep .process-dialog .el-dialog__header { background-color: #2c4f79 !important; padding: 12px 20px; border-bottom: 1px solid #1f3a57; border-radius: 8px 8px 0 0; }
::v-deep .process-dialog .el-dialog__title { color: #ffffff !important; font-size: 16px; font-weight: 600; }
::v-deep .process-dialog .el-dialog__headerbtn .el-dialog__close { color: #ffffff !important; &:hover { color: #e6e6e6 !important; } }
::v-deep .process-dialog .el-dialog__body { padding: 20px 20px; }

/* ===== 响应式适配 ===== */
@media (max-width: 1200px) { .left-panels { width: 280px; min-width: 280px; } }
@media (max-width: 992px) { .left-panels { width: 240px; min-width: 240px; } }
@media (max-width: 768px) { .top-area .search-panel { flex-wrap: wrap; .search-middle { flex: 1; width: 100%; order: 3; } } .left-panels { width: 180px; min-width: 180px; } }
</style>